import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";
import { wdqk } from "@/lib/workDocumentQueries";

/**
 * Master v5 Prompt 8A — presence transport. Server-Sent Events is the
 * primary channel (one-way push of block_locked/block_released/block_saved
 * events, permission-filtered server-side, never revealing holder
 * identity). The access token itself is never placed in the stream URL —
 * a normal, header-authenticated POST mints a short-lived, single-use,
 * document-scoped ticket first, and only that opaque ticket goes on the
 * querystring. `useWorkDocumentDetail`'s own `refetchInterval: 8000` is the
 * polling fallback already in place — if the EventSource connection fails
 * (proxy without SSE support, network hiccup) this hook simply stops
 * retrying and the document keeps refreshing every 8s regardless, so lock
 * state is never stuck longer than that.
 */
export function useBlockPresence({ workspaceId, docId, enabled }) {
  const qc = useQueryClient();
  const sourceRef = useRef(null);

  useEffect(() => {
    if (!enabled || !workspaceId || !docId) return undefined;
    let closed = false;
    let source;

    (async () => {
      let ticket;
      try {
        const res = await rrFetch(`/api/rehearsal-room/work-documents/${docId}/presence/stream-ticket?wrk_id=${workspaceId}`, { method: "POST" });
        if (!res.ok) return;
        ({ ticket } = await res.json());
      } catch {
        return; // polling fallback covers this
      }
      if (closed || !ticket) return;

      const base = process.env.REACT_APP_BACKEND_URL;
      const url = `${base}/api/rehearsal-room/work-documents/${docId}/presence/stream?wrk_id=${workspaceId}&ticket=${encodeURIComponent(ticket)}`;
      try {
        source = new window.EventSource(url);
      } catch {
        return;
      }
      if (closed) {
        source.close();
        return;
      }
      sourceRef.current = source;
      source.onmessage = () => {
        qc.invalidateQueries({ queryKey: wdqk.detail(workspaceId, docId) });
      };
      source.onerror = () => {
        source.close(); // stop retrying; the 8s poll remains the fallback
      };
    })();

    return () => {
      closed = true;
      if (sourceRef.current) sourceRef.current.close();
    };
  }, [enabled, workspaceId, docId, qc]);
}
